import React, { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Languages } from "lucide-react";
import { PhotoField } from "./PhotoField";

const LANG_TABS = [["bg", "БГ"], ["en", "EN"], ["de", "DE"], ["fr", "FR"]];
export const fieldKey = (field, lang) => (lang === "bg" ? (field === "name" ? "name_bg" : field) : `${field}_${lang}`);

export const LangTabs = ({ lang, setLang, testid }) => (
  <div className="flex gap-1">
    {LANG_TABS.map(([k, l]) => (
      <button key={k} type="button" onClick={() => setLang(k)} data-testid={`${testid}-lang-${k}`}
        className={`px-3 py-1 rounded-lg text-xs font-semibold ${lang === k ? "bg-forest text-white" : "bg-cream-muted text-forest"}`}>{l}</button>
    ))}
  </div>
);

export async function aiTranslateFields(obj, fields) {
  const out = {};
  for (const f of fields) {
    const src = obj[fieldKey(f, "bg")];
    if (!src) continue;
    const { data } = await api.post("/ai/translate-text", { text: src });
    ["en", "de", "fr"].forEach((l) => { if (data[l]) out[`${f}_${l}`] = data[l]; });
  }
  return out;
}

const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between"><Label>{label}</Label>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} /></div>
);

export const ProductDialog = ({ value, setValue, categories, onSave }) => {
  const [aiBusy, setAiBusy] = useState(false);
  const [lang, setLang] = useState("bg");
  const set = (patch) => setValue({ ...value, ...patch });
  const translate = async () => {
    setAiBusy(true);
    try { set(await aiTranslateFields(value, ["name", "description", "group"])); toast.success("Преводът е готов – проверете и запазете"); } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  const genDesc = async () => {
    if (!value.name_bg) return toast.error("Въведете име първо");
    const cat = categories.find((c) => c.id === value.category_id);
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-description", {
        kind: "product", name: value.name_bg, name_bg: value.name_bg, context: cat ? (cat.name_bg || cat.name) : "",
      });
      set({ description: data.description_bg || data.description });
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && setValue(null)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{value?.id ? "Редактиране на ястие" : "Ново ястие"}</DialogTitle></DialogHeader>
        {value && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <LangTabs lang={lang} setLang={setLang} testid="product" />
              <button type="button" onClick={translate} disabled={aiBusy || !value.name_bg} className="flex items-center gap-1 text-xs text-gold-dark hover:text-forest disabled:opacity-50" data-testid="product-ai-translate">
                {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />} AI превод EN/DE/FR
              </button>
            </div>
            <div><Label className="text-xs">Име ({lang.toUpperCase()})</Label>
              <Input value={value[fieldKey("name", lang)] || ""} placeholder={lang !== "bg" ? value.name_bg : ""} onChange={(e) => set({ [fieldKey("name", lang)]: e.target.value })} data-testid="product-name" /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Описание / съставки ({lang.toUpperCase()})</Label>
                {lang === "bg" && <button type="button" onClick={genDesc} disabled={aiBusy}
                  className="flex items-center gap-1 text-xs text-gold-dark hover:text-forest disabled:opacity-50" data-testid="ai-gen-product-desc">
                  {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Генерирай с AI
                </button>}
              </div>
              <Textarea value={value[fieldKey("description", lang)] || ""} placeholder={lang !== "bg" ? value.description : ""} onChange={(e) => set({ [fieldKey("description", lang)]: e.target.value })} data-testid="product-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Цена (€)</Label>
                <Input type="number" step="0.01" min="0" value={value.price} onChange={(e) => set({ price: e.target.value })} data-testid="product-price" /></div>
              <div><Label className="text-xs">Грамаж</Label>
                <Input value={value.weight || ""} placeholder="напр. 350 г" onChange={(e) => set({ weight: e.target.value })} data-testid="product-weight" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Категория</Label>
                <Select value={value.category_id} onValueChange={(v) => set({ category_id: v })}>
                  <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_bg || c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Подгрупа ({lang.toUpperCase()}, по избор)</Label>
                <Input value={value[fieldKey("group", lang)] || ""} placeholder="напр. Топли гарнитури" onChange={(e) => set({ [fieldKey("group", lang)]: e.target.value })} data-testid="product-group" /></div>
            </div>
            <PhotoField value={value.image} onChange={(image) => set({ image })} />
            <Toggle label="Наличен (видим за гости и сервитьори)" checked={value.available} onChange={(v) => set({ available: v })} testid="product-available" />
            <Toggle label="Показва се на сайта" checked={value.show_on_website} onChange={(v) => set({ show_on_website: v })} testid="product-website" />
            <Toggle label="Изпраща се в кухнята" checked={value.send_to_kitchen} onChange={(v) => set({ send_to_kitchen: v })} testid="product-kitchen" />
          </div>
        )}
        <DialogFooter><Button onClick={onSave} className="bg-forest text-white rounded-xl" data-testid="save-product">Запази и публикувай</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
